import { uniq } from 'lodash';
import { getJobQueue, getNumCores } from 'utils/conchUtils';
import { isAzure } from 'utils/environmentUtils';

import { InstanceSize, ReportRequestState } from '../model/types';

export type Box = '2x' | '4x' | '8x' | '16x';

export const BOXES: Box[] = ['2x', '4x', '8x', '16x'];

export interface ResolvedInstance {
  box: Box;
  numCores: number;
  /** Why `auto` chose this box; `null` for an explicit choice or the plain default. */
  reason: string | null;
}

export const BOX_LABELS: Record<Box, string> = {
  '2x': '2x large',
  '4x': '4x large',
  '8x': '8x large',
  '16x': '16x large',
};

/**
 * Memory the report container gets, from conchBatch's `resourceRequirements`
 * (src/routes/batch/submitJob.js): 80% of the instance's, not all of it.
 */
export const CONTAINER_MEMORY_GB: Record<Box, number> = {
  '2x': 51,
  '4x': 102,
  '8x': 205,
  '16x': 410,
};

/** Batch queues are AWS only; Azure runs every report in one fixed-size pod. */
export const instanceSizeApplies = (
  environment: SensoryLinkEnvironment | null,
): boolean => !!environment && !isAzure(environment);

/**
 * Job definitions a super admin may run. Each image checks it is connected to
 * its own database (GastrographPackage `GG_Check_Correct_DB_Connection`):
 * main and beta use production, dev and staging their own. So the only safe
 * swap is beta from AWS production. Empty means no override.
 */
export const jobDefinitionChoices = (
  environment: SensoryLinkEnvironment | null,
): conch.JobDefinition[] =>
  environment === 'production' ? ['gg-report-main', 'gg-report-beta'] : [];

/**
 * `numCores` per box. `getJobQueue` maps them back to the queue (<= 8 -> 2x,
 * <= 16 -> 4x, <= 32 -> 8x, more -> 16x). Optimizations have always kept one
 * core free (15 on 4x), so they do the same on every box. All values are in
 * the schema's `numCores` enum.
 */
const SURVEY_CORES: Record<Box, number> = {
  '2x': 8,
  '4x': 16,
  '8x': 31,
  '16x': 63,
};
const OPTIMIZATION_CORES: Record<Box, number> = {
  '2x': 7,
  '4x': 15,
  '8x': 31,
  '16x': 63,
};

const LARGE_CATEGORIES = ['soda', 'chip'];
const LARGE_PRODUCT_COUNT = 25;

const isOptimization = (state: Pick<ReportRequestState, 'reportKind'>) =>
  state.reportKind === 'optimization' || state.reportKind === 'mix_and_match';

/** What Auto does for this report type, for the option's description. */
export const autoRule = (
  state: Pick<ReportRequestState, 'reportKind'>,
): string =>
  isOptimization(state)
    ? 'Optimizations and mix & match always use 4x large.'
    : 'Uses 8x large for bootstrap PQ, soda or chip categories, or more than 25 products, otherwise 4x large.';

const autoReason = (
  state: ReportRequestState,
  names: string[],
): string | null => {
  if (state.advanced.bootstrap_pq) return 'bootstrap PQ is on';
  const large = state.categories.find((c) => LARGE_CATEGORIES.includes(c));
  if (large) return `the ${large} category is included`;
  if (names.length > LARGE_PRODUCT_COUNT)
    return `there are more than ${LARGE_PRODUCT_COUNT} products`;
  return null;
};

export const coresFor = (
  state: Pick<ReportRequestState, 'reportKind'>,
  box: Box,
): number => (isOptimization(state) ? OPTIMIZATION_CORES : SURVEY_CORES)[box];

const QUEUE_BOX: Record<conch.JobQueue, Box> = {
  'gg-queue-efs-2xlarge': '2x',
  'gg-queue-efs-4xlarge': '4x',
  'gg-queue-efs-8xlarge': '8x',
  'gg-queue-efs-16xlarge': '16x',
};

/** The box a core count is queued on. */
export const boxForCores = (numCores: number): Box =>
  QUEUE_BOX[getJobQueue(numCores)];

/**
 * Clone and Update keep a template's 8x or 16x box, usually chosen after a run
 * ran out of memory, rather than dropping back to Auto's 4x. Smaller boxes,
 * and templates without a usable `numCores`, start on Auto.
 */
export const instanceSizeFromCores = (numCores: number): InstanceSize => {
  if (!Number.isFinite(numCores)) return 'auto';
  const box = boxForCores(numCores);
  return box === '8x' || box === '16x' ? box : 'auto';
};

/** The box and core count a request runs with. The Batch queue is derived from `numCores`. */
export const resolveInstance = (
  state: ReportRequestState,
): ResolvedInstance => {
  if (state.instanceSize !== 'auto') {
    return {
      box: state.instanceSize,
      numCores: coresFor(state, state.instanceSize),
      reason: null,
    };
  }
  if (isOptimization(state)) {
    return { box: '4x', numCores: OPTIMIZATION_CORES['4x'], reason: null };
  }
  // An update's competitive set also holds its original products, some of which may not have resolved.
  const names = uniq([
    ...(state.reportKind === 'update_products'
      ? state.updateSource?.originalProducts ?? []
      : []),
    ...state.products.map((p) => p.name),
  ]);
  const numCores = getNumCores(
    state.categories,
    names,
    state.advanced.bootstrap_pq,
  );
  const box = boxForCores(numCores);
  return {
    box,
    numCores,
    reason: box === '8x' ? autoReason(state, names) : null,
  };
};

/** e.g. "Auto: 8x large (31 cores), because bootstrap PQ is on" or "4x large (16 cores)". */
export const describeInstance = (state: ReportRequestState): string => {
  const resolved = resolveInstance(state);
  const box = `${BOX_LABELS[resolved.box]} (${resolved.numCores} cores)`;
  const text = resolved.reason ? `${box}, because ${resolved.reason}` : box;
  return state.instanceSize === 'auto' ? `Auto: ${text}` : text;
};

/** When a manual choice is smaller than Auto's, say what Auto would use and why. */
export const autoWouldUpsize = (state: ReportRequestState): string | null => {
  if (state.instanceSize === 'auto') return null;
  const auto = resolveInstance({ ...state, instanceSize: 'auto' });
  if (BOXES.indexOf(auto.box) <= BOXES.indexOf(state.instanceSize)) return null;
  return `Auto would use ${BOX_LABELS[auto.box]}${auto.reason ? `, because ${auto.reason}` : ''}.`;
};
